import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SEARCH_SESSION_STORAGE_KEY } from "../../../shared/utils/searchUiStorage";

export const useSearchSession = () => {
  const { t, i18n } = useTranslation();

  const buildWelcomeMessage = useCallback(
    (id = Date.now()) => ({
      id,
      type: "system",
      variant: "welcome",
      content: t("app.welcomeMessage"),
    }),
    [t],
  );

  const loadPersistedState = useCallback(() => {
    const persistedState = sessionStorage.getItem(SEARCH_SESSION_STORAGE_KEY);
    if (persistedState) {
      try {
        const parsed = JSON.parse(persistedState);
        if (!parsed || typeof parsed !== "object") {
          sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
          return null;
        }
        return {
          messages: Array.isArray(parsed.messages) ? parsed.messages : [],
          currentSearchId:
            typeof parsed.currentSearchId === "string"
              ? parsed.currentSearchId
              : null,
          isHistoryView: parsed.isHistoryView === true,
        };
      } catch (e) {
        console.error("Failed to parse persisted state:", e);
        sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
      }
    }
    return null;
  }, []);

  const persistedState = useMemo(() => loadPersistedState(), [loadPersistedState]);

  const [messages, setMessages] = useState(() => {
    const msgs = persistedState?.messages || [];
    return msgs.map((msg, idx) => ({
      ...msg,
      id: msg.id || Date.now() + idx,
      variant:
        msg.variant || (idx === 0 && msg.type === "system" ? "welcome" : msg.variant),
    }));
  });

  const [currentSearchId, setCurrentSearchId] = useState(() => {
    if (persistedState?.currentSearchId) {
      return persistedState.currentSearchId;
    }

    const msgs = persistedState?.messages || [];
    for (const msg of msgs) {
      if (msg.type === "result" && msg.papers && msg.papers.length > 0) {
        for (const paper of msg.papers) {
          if (paper.search_id) {
            return paper.search_id;
          }
        }
      }
    }

    return null;
  });

  const [isHistoryView, setIsHistoryView] = useState(
    () => persistedState?.isHistoryView === true,
  );
  const [hasSearchedInSession, setHasSearchedInSession] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    if (
      !isResetting &&
      (messages.length > 1 ||
        (messages.length === 1 && messages[0].type !== "system"))
    ) {
      try {
        sessionStorage.setItem(
          SEARCH_SESSION_STORAGE_KEY,
          JSON.stringify({ messages, currentSearchId, isHistoryView }),
        );
      } catch (error) {
        console.error("Failed to persist search state:", error);
      }
    }
  }, [messages, currentSearchId, isHistoryView, isResetting]);

  useEffect(() => {
    if (!persistedState?.messages || persistedState.messages.length === 0) {
      setMessages([buildWelcomeMessage()]);
    }
    try {
      sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear persisted state:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length > 0 && prev[0].type === "system") {
        const currentWelcome = t("app.welcomeMessage");
        if (prev[0].content !== currentWelcome || prev[0].variant !== "welcome") {
          return [
            {
              ...buildWelcomeMessage(prev[0].id || Date.now()),
              content: currentWelcome,
            },
            ...prev.slice(1),
          ];
        }
      }
      return prev;
    });
  }, [buildWelcomeMessage, t, i18n.language]);

  const handleStartNewSession = useCallback((event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    setIsResetting(true);
    try {
      sessionStorage.removeItem(SEARCH_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear persisted state during reset:", error);
    }

    setMessages([buildWelcomeMessage(`welcome-${Date.now()}`)]);

    setCurrentSearchId(null);
    setIsHistoryView(false);
    setHasSearchedInSession(false);

    if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    setTimeout(() => {
      setIsResetting(false);
    }, 100);
  }, [buildWelcomeMessage]);

  return {
    messages,
    setMessages,
    currentSearchId,
    setCurrentSearchId,
    isHistoryView,
    setIsHistoryView,
    hasSearchedInSession,
    setHasSearchedInSession,
    isResetting,
    handleStartNewSession,
  };
};
